import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'

/**
 * «НАПОМНИ МНЕ»
 *
 * Автоматические напоминания — о таблетках, приёмах у врача, сроках
 * документов — приходят сами. Это про другое: про то, о чём приложение
 * само догадаться не может. «Напомни за день до поездки», «дёрни меня
 * вечером про этот список».
 *
 * Напоминание всегда только себе. «Напомнить жене купить хлеб» — это
 * не напоминание, а поручение, и для него есть дела в планере.
 *
 * Готовые сроки вместо выбора даты: в девяти случаях из десяти нужно
 * «через час» или «завтра утром», а лезть в календарь ради этого —
 * работа, которую никто делать не станет.
 */

type Reminder = { id: string; remindAt: string; message: string | null }

const CHOICES: { name: string; at: () => Date }[] = [
  {
    name: 'Через час',
    at: () => new Date(Date.now() + 3_600_000),
  },
  {
    name: 'Сегодня вечером',
    at: () => {
      const date = new Date()
      date.setHours(19, 0, 0, 0)
      // Если вечер уже прошёл, «сегодня вечером» — это завтра
      if (date.getTime() < Date.now()) date.setDate(date.getDate() + 1)
      return date
    },
  },
  {
    name: 'Завтра утром',
    at: () => {
      const date = new Date()
      date.setDate(date.getDate() + 1)
      date.setHours(9, 0, 0, 0)
      return date
    },
  },
  {
    name: 'Через неделю',
    at: () => {
      const date = new Date()
      date.setDate(date.getDate() + 7)
      date.setHours(9, 0, 0, 0)
      return date
    },
  },
]

export function RemindMe({
  entityType,
  entityId,
  title,
}: {
  entityType: string
  entityId: string
  /** Что написать в уведомлении: через неделю название вспомнится не сразу */
  title: string
}) {
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ reminders: (Reminder & { entityId: string })[] }>('/api/feed/reminders')
      .then((data) => setReminders(data.reminders.filter((r) => r.entityId === entityId)))
      .catch(() => setReminders([]))
  }

  useEffect(load, [entityId])

  const add = async (at: Date) => {
    setBusy(true)
    setError(null)
    try {
      await api('/api/feed/reminders', {
        method: 'POST',
        body: {
          entityType,
          entityId,
          remindAt: at.toISOString(),
          message: title.slice(0, 200),
        },
      })
      setCustom('')
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const drop = async (id: string) => {
    setReminders((current) => current.filter((r) => r.id !== id))
    await api(`/api/feed/reminders/${id}`, { method: 'DELETE' }).catch(load)
  }

  return (
    <div className="remind">
      {reminders.length > 0 && (
        <div className="remind-set">
          {reminders.map((reminder) => (
            <span key={reminder.id} className="remind-chip">
              🔔{' '}
              {new Date(reminder.remindAt).toLocaleString('ru-RU', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
              <button
                className="menu-dish-remove"
                onClick={() => void drop(reminder.id)}
                aria-label="Убрать напоминание"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="remind-choices">
        {CHOICES.map((choice) => (
          <button
            key={choice.name}
            className="day-chip"
            onClick={() => void add(choice.at())}
            disabled={busy}
          >
            {choice.name}
          </button>
        ))}
      </div>

      <div className="add-row" style={{ border: 'none', padding: '6px 0 0' }}>
        <input
          className="input"
          type="datetime-local"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
        <button
          className="btn-primary"
          onClick={() => void add(new Date(custom))}
          disabled={busy || !custom}
        >
          Своё время
        </button>
      </div>

      {error && <div className="sheet-error">{error}</div>}
    </div>
  )
}
